import travelClient from './travelClient';
import { snowflakeAdsTransform } from './adsMemberJson';

export const TRAVEL_ADS_BASE = '/travel/api/v1/advertising';

/**
 * 產生唯一且穩定的 clientRequestId（UUID），防重用
 */
export function generateClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'req-' + Math.random().toString(36).substring(2, 15) + '-' + Date.now();
}

/**
 * 檢查是否為 CAS 409 或業務衝突錯誤
 */
export function isConflictError(error) {
  if (!error) return false;
  return (
    error.httpStatus === 409 ||
    error.code === 95903 ||
    error.raw?.code === 95903 ||
    error.raw?.key === 'travel.advertising.conflict' ||
    String(error.message || '').includes('409') ||
    String(error.message || '').includes('conflict')
  );
}

export const adsMemberApi = {
  promotions: {
    listMine: (params) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/promotions/mine`, {
        params,
        ...snowflakeAdsTransform,
      }),

    get: (id) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/promotions/${id}`, snowflakeAdsTransform),

    create: ({ sourceContentType = 'JOURNEY_PUBLICATION', sourceContentId, clientRequestId }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/promotions`,
        {
          sourceContentType,
          sourceContentId: String(sourceContentId),
          clientRequestId: clientRequestId || generateClientRequestId(),
        },
        snowflakeAdsTransform,
      ),

    submit: (id, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/promotions/${id}/submit`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),

    cancel: (id, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/promotions/${id}/cancel`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),
  },

  creatives: {
    listMine: (params) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/creatives/mine`, {
        params,
        ...snowflakeAdsTransform,
      }),

    listByPromotion: (promotionId) =>
      travelClient.get(
        `${TRAVEL_ADS_BASE}/promotions/${promotionId}/creatives`,
        snowflakeAdsTransform,
      ),

    get: (creativeId) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/creatives/${creativeId}`, snowflakeAdsTransform),

    create: (promotionId, { headline, summary, clientRequestId }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/promotions/${promotionId}/creatives`,
        {
          headline,
          summary,
          clientRequestId: clientRequestId || generateClientRequestId(),
        },
        snowflakeAdsTransform,
      ),

    update: (creativeId, { headline, summary, expectedRowVersion }) =>
      travelClient.patch(
        `${TRAVEL_ADS_BASE}/creatives/${creativeId}`,
        { headline, summary, expectedRowVersion },
        snowflakeAdsTransform,
      ),

    submit: (creativeId, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/creatives/${creativeId}/submit`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),

    cancel: (creativeId, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/creatives/${creativeId}/cancel`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),
  },

  campaigns: {
    listMine: (params) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/campaigns/mine`, {
        params,
        ...snowflakeAdsTransform,
      }),

    get: (id) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/campaigns/${id}`, snowflakeAdsTransform),

    create: (payload) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/campaigns`,
        {
          ...payload,
          clientRequestId: payload.clientRequestId || generateClientRequestId(),
        },
        snowflakeAdsTransform,
      ),

    update: (id, payload) =>
      travelClient.patch(`${TRAVEL_ADS_BASE}/campaigns/${id}`, payload, snowflakeAdsTransform),

    prepare: (id, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/campaigns/${id}/prepare`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),

    activate: (id, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/campaigns/${id}/activate`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),

    pause: (id, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/campaigns/${id}/pause`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),

    cancel: (id, { expectedRowVersion }) =>
      travelClient.post(
        `${TRAVEL_ADS_BASE}/campaigns/${id}/cancel`,
        { expectedRowVersion },
        snowflakeAdsTransform,
      ),
  },

  billing: {
    get: (campaignId) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/campaigns/${campaignId}/billing`, snowflakeAdsTransform),
  },

  reporting: {
    performance: (campaignId, { start, end }) =>
      travelClient.get(`${TRAVEL_ADS_BASE}/reports/campaigns/${campaignId}/performance`, {
        params: { start, end },
        ...snowflakeAdsTransform,
      }),
  },
};
